import { createStyles } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';
import { SlateStatus } from '@betterpool/api-types/games-service';
import { match } from 'ts-pattern';

import Container from '~/domains/design-system/Container';
import { Routes } from '~/domains/common/constants/routes';
import type { FullContest } from '~/domains/contest/domains/services/interfaces/full-contest.interface';
import dt from '~/testing';

import useTeamPickEmIsStandingsBySlateSupported from '../../../../teamPickEm/hooks/useTeamPickEmIsStandingsBySlateSupported';

import {
  GamecenterNonScheduledNavList,
  GamecenterNonScheduledNavListItem,
} from './GamecenterNonScheduledNavComponents';

const useStyles = createStyles((theme) => ({
  container: {
    background: theme.colors.gray[9],
    borderTop: `1px solid ${theme.colors.gray[8]}`,
  },
}));

function TeamPickEmNonScheduledNav({ contest }: { contest: FullContest }) {
  const { t } = useTranslation('contest');
  const { classes } = useStyles();
  const contestId = contest.id;
  const isSlatesSupported = useTeamPickEmIsStandingsBySlateSupported();
  const isAnySlateLive = contest.slates.some((slate) =>
    [SlateStatus.IN_PROGRESS, SlateStatus.LIVE].includes(slate.status)
  );
  const isSpectatorMode = contest?.public_viewing && contest?.entries.user === 0;

  return (
    <div className={classes.container}>
      <Container>
        <GamecenterNonScheduledNavList>
          {isAnySlateLive ? (
            <GamecenterNonScheduledNavListItem
              href={Routes.gamecenterStandingsSlates(contestId)}
              activeForRoutes={[Routes.gamecenterStandingsOverall(contestId)]}
              data-test-id={dt.contest.gamecenter.common.navigation.nonScheduled.standings}
            >
              {t('gamecenter.layoutNonScheduled.nav.standings')}
            </GamecenterNonScheduledNavListItem>
          ) : (
            <GamecenterNonScheduledNavListItem
              href={Routes.gamecenterStandingsOverall(contestId)}
              activeForRoutes={[Routes.gamecenterStandingsSlates(contestId)]}
              data-test-id={dt.contest.gamecenter.common.navigation.nonScheduled.standings}
            >
              {t('gamecenter.layoutNonScheduled.nav.standings')}
            </GamecenterNonScheduledNavListItem>
          )}
          {match({ isSpectatorMode, isSlatesSupported })
            .returnType<React.ReactNode>()
            .with({ isSpectatorMode: true }, () => null)
            .with({ isSlatesSupported: true }, () => (
              <GamecenterNonScheduledNavListItem
                href={Routes.gamecenterEntriesSlates(contestId)}
                activeForRoutes={[Routes.gamecenterEntriesOverall(contestId)]}
                data-test-id={dt.contest.gamecenter.common.navigation.nonScheduled.entries}
              >
                {t('gamecenter.layoutNonScheduled.nav.entries')}
              </GamecenterNonScheduledNavListItem>
            ))
            .with({ isSlatesSupported: false }, () => (
              <GamecenterNonScheduledNavListItem
                href={Routes.gamecenterEntriesOverall(contestId)}
                data-test-id={dt.contest.gamecenter.common.navigation.nonScheduled.entries}
              >
                {t('gamecenter.layoutNonScheduled.nav.entries')}
              </GamecenterNonScheduledNavListItem>
            ))
            .otherwise(() => null)}
          <GamecenterNonScheduledNavListItem
            href={Routes.gamecenterStats(contestId)}
            activeForRoutes={[Routes.gamecenterStatsAvailability(contestId)]}
            data-test-id={dt.contest.gamecenter.common.navigation.nonScheduled.stats}
          >
            {t('gamecenter.layoutNonScheduled.nav.stats')}
          </GamecenterNonScheduledNavListItem>
          <GamecenterNonScheduledNavListItem
            href={Routes.contestDetail(contestId)}
            data-test-id={dt.contest.gamecenter.common.navigation.nonScheduled.detail}
          >
            {t('gamecenter.layoutNonScheduled.nav.detail')}
          </GamecenterNonScheduledNavListItem>
        </GamecenterNonScheduledNavList>
      </Container>
    </div>
  );
}

export default TeamPickEmNonScheduledNav;
